import React, { useState } from 'react';
import { EmailCampaign } from '../../../services/emailService';
import { MoreVertical, Play, Pause, Edit, Copy, Trash2, BarChart3, Mail, MailOpen, MessageCircle } from 'lucide-react';
import Badge from './Badge';

interface CampaignCardProps {
  campaign: EmailCampaign;
  onEdit: (campaign: EmailCampaign) => void;
  onDelete: (campaign: EmailCampaign) => void;
  onToggleStatus: (campaign: EmailCampaign) => void;
  onDuplicate?: (campaign: EmailCampaign) => void;
  onViewStats?: (campaign: EmailCampaign) => void;
}

const CampaignCard: React.FC<CampaignCardProps> = ({
  campaign,
  onEdit,
  onDelete,
  onToggleStatus,
  onDuplicate,
  onViewStats
}) => {
  const [showMenu, setShowMenu] = useState(false);

  const sent = campaign.sent_count || 0;
  const opened = campaign.opened_count || 0;
  const replied = campaign.replied_count || 0;
  const openRate = sent > 0 ? ((opened / sent) * 100).toFixed(1) : '0.0';
  const replyRate = sent > 0 ? ((replied / sent) * 100).toFixed(1) : '0.0';
  const progress = campaign.total_contacts ? Math.round((sent / campaign.total_contacts) * 100) : 0;

  const getStatusBadge = () => {
    switch (campaign.status) {
      case 'active':
        return <Badge variant="success" dot>Active</Badge>;
      case 'paused':
        return <Badge variant="warning" dot>Paused</Badge>;
      case 'completed':
        return <Badge variant="neutral" dot>Completed</Badge>;
      default:
        return <Badge variant="neutral" dot>Draft</Badge>;
    }
  };

  const getTimeAgo = () => {
    if (!campaign.created_at) return 'Just now';
    const date = new Date(campaign.created_at);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffHours / 24);
    
    if (diffHours < 1) return 'Just now';
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  return (
    <div className="bg-white rounded-xl border border-gray-200 hover:border-blue-300 hover:shadow-lg transition-all duration-200 overflow-hidden group">
      {/* Header */}
      <div className="p-6 border-b border-gray-100">
        <div className="flex items-start justify-between mb-3">
          <div className="flex-1 min-w-0">
            <h3 className="text-lg font-semibold text-gray-900 truncate mb-1">
              {campaign.name}
            </h3>
            <p className="text-sm text-gray-600 truncate">
              {campaign.subject || 'No subject'}
            </p>
          </div>
          <div className="flex items-center gap-2 ml-3">
            {getStatusBadge()}
            <div className="relative">
              <button
                onClick={() => setShowMenu(!showMenu)}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors opacity-0 group-hover:opacity-100"
              >
                <MoreVertical className="w-5 h-5 text-gray-600" />
              </button>
              
              {/* Dropdown Menu */}
              {showMenu && (
                <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-10">
                  <button
                    onClick={() => { onEdit(campaign); setShowMenu(false); }}
                    className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                  >
                    <Edit className="w-4 h-4" />
                    Edit
                  </button>
                  {onDuplicate && (
                    <button
                      onClick={() => { onDuplicate(campaign); setShowMenu(false); }}
                      className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                    >
                      <Copy className="w-4 h-4" />
                      Duplicate
                    </button>
                  )}
                  {onViewStats && (
                    <button
                      onClick={() => { onViewStats(campaign); setShowMenu(false); }}
                      className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                    >
                      <BarChart3 className="w-4 h-4" />
                      View Stats
                    </button>
                  )}
                  <div className="border-t border-gray-100 my-1"></div>
                  <button
                    onClick={() => { onDelete(campaign); setShowMenu(false); }}
                    className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50 flex items-center gap-2"
                  >
                    <Trash2 className="w-4 h-4" />
                    Delete
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Progress Bar */}
        <div className="mb-3">
          <div className="flex items-center justify-between text-sm text-gray-600 mb-2">
            <span>Progress</span>
            <span className="font-medium">{progress}%</span>
          </div>
          <div className="w-full bg-gray-100 rounded-full h-2 overflow-hidden">
            <div
              className="bg-blue-600 h-2 rounded-full transition-all duration-500"
              style={{ width: `${progress}%` }}
            ></div>
          </div>
        </div>

        <div className="text-xs text-gray-500">
          {sent.toLocaleString()} / {(campaign.total_contacts || 0).toLocaleString()} sent • {getTimeAgo()}
        </div>
      </div>

      {/* Stats */}
      <div className="p-6">
        <div className="grid grid-cols-3 gap-4">
          <div className="text-center">
            <div className="flex items-center justify-center mb-2">
              <Mail className="w-4 h-4 text-blue-600 mr-1" />
              <span className="text-2xl font-bold text-gray-900">{sent.toLocaleString()}</span>
            </div>
            <p className="text-xs text-gray-600">Sent</p>
          </div>
          
          <div className="text-center border-l border-r border-gray-200">
            <div className="flex items-center justify-center mb-2">
              <MailOpen className="w-4 h-4 text-green-600 mr-1" />
              <span className={`text-2xl font-bold ${
                parseFloat(openRate) > 30 ? 'text-green-600' :
                parseFloat(openRate) > 15 ? 'text-yellow-600' :
                'text-red-600'
              }`}>{openRate}%</span>
            </div>
            <p className="text-xs text-gray-600">Open Rate</p>
          </div>
          
          <div className="text-center">
            <div className="flex items-center justify-center mb-2">
              <MessageCircle className="w-4 h-4 text-purple-600 mr-1" />
              <span className={`text-2xl font-bold ${
                parseFloat(replyRate) > 5 ? 'text-green-600' :
                parseFloat(replyRate) > 2 ? 'text-yellow-600' :
                'text-red-600'
              }`}>{replyRate}%</span>
            </div>
            <p className="text-xs text-gray-600">Reply Rate</p>
          </div>
        </div>
      </div>

      {/* Actions */}
      <div className="px-6 py-4 bg-gray-50 border-t border-gray-100 flex items-center justify-between">
        <button
          onClick={() => onToggleStatus(campaign)}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium text-sm transition-colors ${
            campaign.status === 'active'
              ? 'bg-yellow-100 text-yellow-700 hover:bg-yellow-200'
              : 'bg-green-100 text-green-700 hover:bg-green-200'
          }`}
        >
          {campaign.status === 'active' ? (
            <>
              <Pause className="w-4 h-4" />
              Pause
            </>
          ) : (
            <>
              <Play className="w-4 h-4" />
              Start
            </>
          )}
        </button>

        <div className="text-sm text-gray-600">
          Mailbox: <span className="font-medium text-gray-900">{campaign.mailbox?.name || 'Unknown'}</span>
        </div>
      </div>
    </div>
  );
};

export default CampaignCard;
